import { useParams, Link } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO, articleSchema } from '@/components/shared/SEO';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';

const postContent = {
  'competitor-monitoring-shopify-2025': {
    title: 'The Complete Guide to Competitor Monitoring for Shopify in 2025',
    category: 'Strategy',
    date: 'Jan 15, 2025',
    readTime: '8 min read',
    author: 'RADAR Team',
    content: [
      { type: 'p', text: 'In the fast-paced world of e-commerce, understanding your competition is no longer optional — it is essential. Shopify merchants who monitor their competitors effectively gain a significant strategic advantage.' },
      { type: 'h2', text: 'Why Competitor Monitoring Matters' },
      { type: 'p', text: 'The e-commerce landscape moves faster than ever. New brands launch daily, advertising strategies shift weekly, and pricing can change hourly. Without systematic monitoring, you are making decisions based on outdated information.' },
      { type: 'callout', text: 'Merchants who actively monitor competitors are 3x more likely to identify market opportunities before the competition.' },
      { type: 'h2', text: 'What to Monitor' },
      { type: 'p', text: 'Effective competitor monitoring covers several key areas: advertising activity across Meta, TikTok, and Google; pricing and stock changes; storefront technology decisions; and overall market positioning.' },
      { type: 'h2', text: 'Advertising Intelligence' },
      { type: 'p', text: 'Track which ad creatives your competitors are using, how their copy evolves, and which campaigns they run longest (a strong signal of performance). RADAR automates this across all major platforms.' },
      { type: 'h2', text: 'Pricing & Stock Monitoring' },
      { type: 'p', text: 'Price changes and stock fluctuations reveal competitor strategy in real time. When a competitor drops prices aggressively, it signals either a clearance or a strategic pricing war. When they go out of stock, it is your opportunity.' },
      { type: 'h2', text: 'Getting Started' },
      { type: 'p', text: 'Start by identifying your top 5-10 competitors. Add them to RADAR and let the platform monitor everything automatically. Set up alerts for the signals that matter most to your business.' },
      { type: 'callout', text: 'Start your free 7-day trial of RADAR and see what your competitors are doing right now.' },
    ],
  },
};

const defaultContent = {
  content: [
    { type: 'p', text: 'This article is part of our competitive intelligence series for Shopify merchants. We cover strategies, tools, and tactics for staying ahead in e-commerce.' },
    { type: 'h2', text: 'Coming Soon' },
    { type: 'p', text: 'This article is being written by our team of e-commerce intelligence experts. Check back soon for the full piece.' },
    { type: 'callout', text: 'Want to stay updated? Start your free RADAR trial and join our community of growth-focused merchants.' },
  ],
};

export default function BlogArticlePage() {
  const { slug } = useParams();
  const post = postContent[slug] || {
    title: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    category: 'Article',
    date: 'Jan 2025',
    readTime: '5 min read',
    author: 'RADAR Team',
    ...defaultContent,
  };

  return (
    <MarketingLayout>
      <SEO
        title={post.title}
        description={post.content?.[0]?.text?.slice(0, 155) || `Read ${post.title} on the RADAR blog.`}
        canonical={`/blog/${slug}`}
        ogType="article"
        article={{ publishedTime: post.date, author: post.author, section: post.category }}
        structuredData={articleSchema({
          title: post.title,
          excerpt: post.content?.[0]?.text?.slice(0, 155) || '',
          datePublished: post.date,
        })}
      />
      <Section hero>
        <Container>
          <AnimatedReveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> Back to blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <SignalChip variant="lime">{post.category}</SignalChip>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-foreground mb-4 max-w-[720px]">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{post.author}</span>
              <span>&middot;</span>
              <span>{post.date}</span>
            </div>
          </AnimatedReveal>
        </Container>
      </Section>

      <Section dense>
        <Container>
          <div className="grid lg:grid-cols-4 gap-12">
            <article className="lg:col-span-3 max-w-[720px] space-y-6">
              {post.content.map((block, i) => {
                if (block.type === 'h2') return (
                  <h2 key={i} className="text-xl font-display font-bold text-foreground mt-10 mb-4">{block.text}</h2>
                );
                if (block.type === 'callout') return (
                  <GlassPanel key={i} className="p-6 border-l-2 border-lime-500">
                    <p className="text-sm text-foreground/90 leading-relaxed">{block.text}</p>
                  </GlassPanel>
                );
                return <p key={i} className="text-sm text-muted-foreground leading-[1.8]">{block.text}</p>;
              })}

              <div className="mt-12 pt-8 border-t border-white/[0.06]">
                <GlassPanel className="p-8 text-center">
                  <h3 className="text-lg font-display font-bold text-foreground mb-3">Ready to monitor your competitors?</h3>
                  <p className="text-sm text-muted-foreground mb-6">Start your free 7-day trial of RADAR.</p>
                  <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors">
                    Start free trial <ArrowRight className="h-4 w-4" />
                  </Link>
                </GlassPanel>
              </div>
            </article>

            <aside className="hidden lg:block">
              <GlassPanel className="p-5 sticky top-24">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-4">Related articles</p>
                <div className="space-y-3">
                  {['meta-ads-spy-guide', 'pricing-strategy-intelligence', 'tiktok-ads-ecommerce'].map((s, i) => (
                    <Link key={i} to={`/blog/${s}`} className="block text-sm text-foreground/70 hover:text-lime-400 transition-colors">
                      {s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Link>
                  ))}
                </div>
              </GlassPanel>
            </aside>
          </div>
        </Container>
      </Section>
    </MarketingLayout>
  );
}
